import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { HiShare } from 'react-icons/hi';

const ProductCard = ({ product }) => {
  const { t } = useTranslation();
  const [hoveredColor, setHoveredColor] = useState(null);

  const displayImage = hoveredColor
    ? product.colors.find((c) => c.name === hoveredColor)?.image
    : product.image;

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const productUrl = `${window.location.origin}/store/${product.id}`;
    const shareData = {
      title: product.name,
      text: `Check out this amazing ${product.name}`,
      url: productUrl,
    };
    if (navigator.share) {
      navigator.share(shareData);
    } else {
      navigator.clipboard.writeText(productUrl);
    }
  };

  return (
    <article className="group flex flex-col h-full">
      {/* Image plate */}
      <div className="relative bg-paper-warm aspect-[4/5] overflow-hidden">
        <Link
          to={`/store/${product.id}`}
          className="absolute inset-0 flex items-center justify-center p-8"
          aria-label={product.name}
        >
          <img
            src={displayImage}
            alt={product.name}
            loading="lazy"
            className="max-w-full max-h-full object-contain transition-transform duration-500 ease-editorial group-hover:scale-[1.03]"
          />
        </Link>

        {product.featured && (
          <span className="absolute top-4 left-4 label bg-ink text-paper px-2.5 py-1.5">
            {t('Featured')}
          </span>
        )}

        {product.discount && (
          <span className="absolute top-4 right-4 label tabular bg-gold text-ink px-2.5 py-1.5">
            −{product.discount}%
          </span>
        )}

        {!product.inStock && (
          <span className="absolute inset-0 flex items-center justify-center bg-paper/80">
            <span className="label text-ink">{t('Out of Stock')}</span>
          </span>
        )}

        <button
          type="button"
          onClick={handleShare}
          className="absolute bottom-4 right-4 w-11 h-11 flex items-center justify-center bg-paper border border-[color:var(--rule)] text-ink-faint opacity-0 transition-all duration-200 hover:text-ink focus-visible:opacity-100 group-hover:opacity-100"
          aria-label={`${t('Share')} ${product.name}`}
        >
          <HiShare aria-hidden />
        </button>
      </div>

      {/* Record */}
      <div className="flex flex-col flex-1 pt-4 border-t border-[color:var(--rule)] mt-4">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="display-3">
            <Link to={`/store/${product.id}`} className="hover:text-ink-soft transition-colors">
              {product.name}
            </Link>
          </h3>
          <span className="display-3 tabular shrink-0">
            ${product.price.toFixed(2)}
          </span>
        </div>

        {product.originalPrice && (
          <span className="text-xs text-ink-faint tabular line-through mt-1 self-end">
            ${product.originalPrice.toFixed(2)}
          </span>
        )}

        <p className="mt-2 text-sm leading-relaxed text-ink-soft line-clamp-2">
          {product.description}
        </p>

        {product.priceLocal && (
          <p className="mt-2 text-xs text-ink-faint tabular">
            {product.priceLocal.toLocaleString()} {product.localCurrency}
          </p>
        )}

        {/* Colours — hovering previews the garment above */}
        <div className="flex items-center gap-2 mt-4">
          {product.colors.map((color) => (
            <button
              key={color.name}
              type="button"
              onMouseEnter={() => setHoveredColor(color.name)}
              onMouseLeave={() => setHoveredColor(null)}
              onFocus={() => setHoveredColor(color.name)}
              onBlur={() => setHoveredColor(null)}
              className="w-6 h-6 rounded-full border transition-transform duration-200 hover:scale-110"
              style={{
                backgroundColor: color.hex,
                borderColor:
                  hoveredColor === color.name ? 'var(--ink)' : 'var(--rule-strong)',
              }}
              aria-label={`${t('Preview')} ${color.name}`}
              title={color.name}
            />
          ))}
        </div>

        <p className="mt-4 pt-3 border-t border-[color:var(--rule)] text-xs text-ink-faint">
          {product.sizes?.join(' · ')}
          {product.material ? `  —  ${product.material}` : ''}
        </p>
      </div>
    </article>
  );
};

export default ProductCard;
